// The restaurant's live events (STEP 3, docs/DINING_API_2026-10-28.md "Real time").
//
// ONE connection per tab and sign-in, shared by every subscriber:
// - Server-Sent Events from GET /api/dining/events/stream, opened with a
//   60-second single-use ticket (POST /api/dining/events/stream-ticket) because
//   EventSource cannot send the Authorization header — the sign-in token never
//   goes in a URL;
// - resumes from the last seq it delivered (the log is gap-free per restaurant),
//   so a Wi-Fi drop, a sleeping tablet or a server restart loses nothing;
// - reconnects with backoff; while the stream is down it polls
//   GET /api/dining/events?after=<seq> (the same events, the same cursor);
// - a cursor older than the 7-day log (410) tells subscribers to RELOAD their
//   state (onReset) and continues from the latest seq;
// - a sign-in that is no longer valid (401 / 403 / `signed_out`) stops it.
// Screens hold no state of their own beyond what the server answers: load the
// state (floor, tickets, checks), then apply events newer than what you hold.
import { useEffect, useRef, useState } from 'react';
import { API } from '@/config/api';
import { getAuthToken } from '@/lib/authToken';

const RECONNECT_MS = [1000, 2000, 5000, 10000, 20000, 30000];
const POLL_MS = 3000;
const POLL_LIMIT = 500;

export const STATUS = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  LIVE: 'live', // the stream is open
  POLLING: 'polling', // the stream is down: polling until it is back
  SIGNED_OUT: 'signed_out',
  STOPPED: 'stopped',
};

class SignedOut extends Error {}

class Expired extends Error {
  constructor(detail) {
    super('cursor_expired');
    this.detail = detail || {};
  }
}

function headersFor({ getToken, merchantId, deviceId }) {
  const token = getToken();
  if (!token) throw new SignedOut('no sign-in');
  const headers = { Authorization: `Bearer ${token}` };
  if (merchantId) headers['X-Merchant-ID'] = String(merchantId);
  if (deviceId) headers['X-Device-ID'] = String(deviceId);
  return headers;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
}

class DiningEvents {
  constructor(options) {
    this.options = { getToken: getAuthToken, ...options };
    this.lastSeq = Number.isInteger(options.after) ? options.after : null;
    this.status = STATUS.IDLE;
    this.listeners = new Set(); // (event) => void
    this.resetListeners = new Set(); // ({latest_seq, pruned_through_seq, code}) => void
    this.statusListeners = new Set(); // (status) => void
    this.source = null;
    this.attempt = 0;
    this.reconnectTimer = null;
    this.pollTimer = null;
    this.polling = false;
    this.stopped = true;
    this.onOnline = () => this.reconnectNow();
  }

  // ------------------------------------------------------------ subscribers

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onReset(listener) {
    this.resetListeners.add(listener);
    return () => this.resetListeners.delete(listener);
  }

  onStatus(listener) {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((fn) => fn(status));
  }

  deliver(event) {
    if (!event || !Number.isInteger(event.seq)) return;
    if (this.lastSeq !== null && event.seq <= this.lastSeq) return; // already delivered (replay overlap)
    this.lastSeq = event.seq;
    this.listeners.forEach((fn) => {
      try {
        fn(event);
      } catch (e) {
        // one screen's handler never stops the others
        console.error('[diningEvents] subscriber failed', e);
      }
    });
  }

  reset(detail) {
    const latest = Number.isInteger(detail?.latest_seq) ? detail.latest_seq : 0;
    this.lastSeq = latest;
    this.resetListeners.forEach((fn) => {
      try {
        fn(detail || {});
      } catch (e) {
        console.error('[diningEvents] reset handler failed', e);
      }
    });
  }

  // ------------------------------------------------------------ life cycle

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    if (typeof window !== 'undefined') window.addEventListener('online', this.onOnline);
    this.connect();
  }

  stop(status = STATUS.STOPPED) {
    this.stopped = true;
    this.closeSource();
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.pollTimer);
    this.reconnectTimer = null;
    this.pollTimer = null;
    if (typeof window !== 'undefined') window.removeEventListener('online', this.onOnline);
    this.setStatus(status);
  }

  closeSource() {
    if (this.source) {
      this.source.close();
      this.source = null;
    }
  }

  reconnectNow() {
    if (this.stopped) return;
    clearTimeout(this.reconnectTimer);
    this.attempt = 0;
    this.connect();
  }

  scheduleReconnect() {
    if (this.stopped) return;
    clearTimeout(this.reconnectTimer);
    const wait = RECONNECT_MS[Math.min(this.attempt, RECONNECT_MS.length - 1)];
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), wait);
    this.startPolling();
  }

  fail(error) {
    if (error instanceof SignedOut) {
      this.stop(STATUS.SIGNED_OUT);
      return true;
    }
    if (error instanceof Expired) {
      this.reset(error.detail);
      return false;
    }
    return false;
  }

  // ------------------------------------------------------------ the cursor

  async ensureCursor() {
    if (this.lastSeq !== null) return;
    // no cursor given: live from now (the screen loaded its state just before)
    const response = await fetch(`${API}/dining/events?after=0&limit=1`, { headers: headersFor(this.options) });
    const body = await readJson(response);
    if (response.status === 401 || response.status === 403) throw new SignedOut();
    if (response.status === 410) {
      this.lastSeq = body?.detail?.latest_seq ?? 0;
      return;
    }
    if (!response.ok) throw new Error(`events ${response.status}`);
    this.lastSeq = body?.latest_seq ?? 0;
  }

  // ------------------------------------------------------------ polling (fallback)

  async pollOnce() {
    let more = true;
    while (more && !this.stopped) {
      const response = await fetch(`${API}/dining/events?after=${this.lastSeq}&limit=${POLL_LIMIT}`, {
        headers: headersFor(this.options),
      });
      const body = await readJson(response);
      if (response.status === 401 || response.status === 403) throw new SignedOut();
      if (response.status === 410) throw new Expired(body?.detail);
      if (!response.ok) throw new Error(`events ${response.status}`);
      if (Number.isInteger(body?.latest_seq) && body.latest_seq < this.lastSeq) {
        // the cursor is ahead of the restaurant's log (it was restored): reload
        throw new Expired({ code: 'cursor_ahead', latest_seq: body.latest_seq });
      }
      (body?.events || []).forEach((event) => this.deliver(event));
      more = !!body?.has_more;
    }
  }

  startPolling() {
    if (this.polling || this.stopped) return;
    this.polling = true;
    this.setStatus(STATUS.POLLING);
    const tick = async () => {
      if (!this.polling || this.stopped) return;
      try {
        await this.ensureCursor();
        await this.pollOnce();
      } catch (error) {
        if (this.fail(error)) return;
      }
      if (this.polling && !this.stopped) this.pollTimer = setTimeout(tick, POLL_MS);
    };
    tick();
  }

  stopPolling() {
    this.polling = false;
    clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  // ------------------------------------------------------------ the stream

  async connect() {
    if (this.stopped) return;
    this.closeSource();
    if (typeof EventSource === 'undefined') {
      this.startPolling(); // no SSE in this browser: polling is the transport
      return;
    }
    if (!this.polling) this.setStatus(STATUS.CONNECTING);
    let ticket;
    try {
      await this.ensureCursor();
      const response = await fetch(`${API}/dining/events/stream-ticket`, {
        method: 'POST',
        headers: headersFor(this.options),
      });
      if (response.status === 401 || response.status === 403) throw new SignedOut();
      if (!response.ok) throw new Error(`stream-ticket ${response.status}`);
      ticket = (await readJson(response))?.ticket;
      if (!ticket) throw new Error('stream-ticket: no ticket');
    } catch (error) {
      if (!this.fail(error)) this.scheduleReconnect();
      return;
    }
    if (this.stopped) return;

    const url = `${API}/dining/events/stream?after=${this.lastSeq}&ticket=${encodeURIComponent(ticket)}`;
    const source = new EventSource(url);
    this.source = source;

    source.addEventListener('hello', () => {
      this.attempt = 0;
      this.stopPolling();
      this.setStatus(STATUS.LIVE);
    });
    source.onmessage = (message) => {
      try {
        this.deliver(JSON.parse(message.data));
      } catch (e) {
        console.error('[diningEvents] unreadable event', e);
      }
    };
    source.addEventListener('reset', (message) => {
      let detail = {};
      try {
        detail = JSON.parse(message.data);
      } catch (e) {
        // keep {}
      }
      this.closeSource();
      this.reset(detail);
      this.reconnectNow();
    });
    source.addEventListener('signed_out', () => this.stop(STATUS.SIGNED_OUT));
    source.addEventListener('bye', () => {
      // the server ends a stream after 15 minutes: a new ticket, same cursor
      this.closeSource();
      this.reconnectNow();
    });
    source.onerror = () => {
      // A ticket is single use, so the browser's own retry cannot work: close,
      // catch up by polling (which also tells a 410 / 401 apart) and reconnect.
      if (this.source !== source) return;
      this.closeSource();
      this.pollOnce()
        .catch((error) => this.fail(error))
        .finally(() => this.scheduleReconnect());
    };
  }
}

// ------------------------------------------------------------ shared instance

const shared = new Map(); // key -> {client, users}

function keyOf({ merchantId, deviceId, getToken }) {
  const token = (getToken || getAuthToken)() || '';
  return `${token}|${merchantId || ''}|${deviceId || ''}`;
}

/**
 * The tab's connection for this sign-in (+ location). Call release() when done;
 * the connection closes when its last user releases it.
 * @param {{merchantId?: string|number, deviceId?: string, after?: number, getToken?: () => string|null}} options
 */
export function connectDiningEvents(options = {}) {
  const key = keyOf(options);
  let entry = shared.get(key);
  if (!entry) {
    entry = { client: new DiningEvents(options), users: 0 };
    shared.set(key, entry);
  } else if (Number.isInteger(options.after) && entry.client.lastSeq === null) {
    entry.client.lastSeq = options.after;
  }
  entry.users += 1;
  entry.client.start();
  let released = false;
  return {
    client: entry.client,
    release() {
      if (released) return;
      released = true;
      entry.users -= 1;
      if (entry.users <= 0) {
        entry.client.stop();
        shared.delete(key);
      }
    },
  };
}

/**
 * React hook: live restaurant events.
 * @param {{onEvent?: (event) => void, onReset?: (detail) => void, types?: string[],
 *          after?: number, merchantId?: string|number, deviceId?: string, enabled?: boolean,
 *          getToken?: () => string|null}} options
 *   onEvent — each event once, in seq order (filtered by `types` when given);
 *   onReset — the cursor is gone: reload the screen's state;
 *   after   — the latest_seq the screen's state was loaded at (else: live from now).
 * @returns {{status: string, lastSeq: number|null}}
 */
export function useDiningEvents({ onEvent, onReset, types, after, merchantId, deviceId, enabled = true, getToken } = {}) {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [lastSeq, setLastSeq] = useState(Number.isInteger(after) ? after : null);
  const handlers = useRef({ onEvent, onReset, types });
  handlers.current = { onEvent, onReset, types };
  const typeKey = (types || []).join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    const { client, release } = connectDiningEvents({ after, merchantId, deviceId, ...(getToken ? { getToken } : {}) });
    const offEvent = client.subscribe((event) => {
      setLastSeq(event.seq);
      const { onEvent: handle, types: wanted } = handlers.current;
      if (handle && (!wanted || wanted.length === 0 || wanted.includes(event.type))) handle(event);
    });
    const offReset = client.onReset((detail) => {
      setLastSeq(Number.isInteger(detail?.latest_seq) ? detail.latest_seq : null);
      if (handlers.current.onReset) handlers.current.onReset(detail);
    });
    const offStatus = client.onStatus(setStatus);
    return () => {
      offEvent();
      offReset();
      offStatus();
      release();
    };
    // `after` is only the starting cursor: a new value does not reconnect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, merchantId, deviceId, typeKey, getToken]);

  return { status, lastSeq };
}

export default useDiningEvents;
